import { auth } from "config/firebase.config";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { listenToUser } from "firebaseService/firebaseService";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

export const AuthProvider = ({ children, user: initialUser }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const router = useNavigate();

    const signin = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);

            if (userCredential.user) {
                setCurrentUser(userCredential.user);
            }
            router("/dashboard");
        } catch (error) {
            console.error("Sign in error:", error);
            throw new Error("Failed to sign in. Please check your email and password.");
        }
    }

    useEffect(() => {
        setIsAuthReady(false);
        if (!initialUser) {
            setCurrentUser(null);
            setCurrentUserData(null);
            setIsAuthReady(true);
            return;
        }

        const uid = initialUser?.uid || currentUser?.uid;
        if (!uid) return;

        const unsubscribe = listenToUser(uid, (userData) => {
            setCurrentUserData(userData || null);
            setIsAuthReady(true);
        });

        setCurrentUser(initialUser || currentUser);

        return () => unsubscribe();
    }, [initialUser, currentUser, router]);

    const signout = () => {
        signOut(auth)
            .then(() => {
                router("/sign-in");
            })
            .catch((error) => {
                console.error("Sign out error:", error);
            })
    }

    const value = {
        signin,
        signout,
        isAuthReady,
        initialUser,
        currentUser,
        currentUserData,
        authenticated: !!currentUser,
    }

    return <AuthContext.Provider value={value}>
        {children}
    </AuthContext.Provider>;
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
};

export const AuthConsumer = AuthContext.Consumer;